/**
 * Tarelkali kolonna gidravlikasi (soddalashtirilgan): bug' tezligi va to'lib ketish.
 *
 * MANBA: Kasatkin, gl. XVII ("Скорость пара в колонне", w = C·√(ρж/ρп) ko'rinishi);
 *        Pavlov, Romankov, Noskov, gl. 7 (kolonna diametri hisobi);
 *        Fair J.R. (1961) — Souders-Brown C_sb korrelyatsiyasi; Perry's 8th ed. Sec. 14;
 *        Lygeros & Magoulas (1986) — Fair diagrammasining analitik approksimatsiyasi.
 *
 * TENGLAMALAR:
 *   Bug' zichligi (ideal gaz):   ρ_V = P·M_V/(R·T),  M_V = y·M1 + (1−y)·M2
 *   Suyuqlik zichligi:           1/ρ_L = Σ w_i/ρ_i(T)     (hajm qo'shiluvchanligi)
 *   Bug' tezligi:                u = V·M_V/(ρ_V·A_net),   A_net = (1 − f_dc)·πD²/4
 *   Oqim parametri:              F_lv = (L_m/V_m)·√(ρ_V/ρ_L)      (massa sarflari)
 *   Fair C_sb (m/s), TS mm:      C_sb = 0.0105 + 8.127e-4·TS^0.755·exp(−1.463·F_lv^0.842)
 *   Souders-Brown:               u_flood = C_sb·(σ/0.02)^0.2·√((ρ_L − ρ_V)/ρ_V)
 *
 * ASSUMPTION:
 *   1. Elakli/qalpoqli tarelka farqi yo'q; C_sb Fair (elakli tarelka) bo'yicha.
 *   2. Sirt tarangligi σ param (standart 0.02 N/m), haroratga bog'liq emas.
 *   3. Downcomer ulushi f_dc (standart 0.12) — kesim to'g'ridan-to'g'ri kamaytiriladi.
 *   4. Ko'pik (foaming) tuzatish koeffitsienti = 1.
 *   5. Tezlik ikki seksiyada (tepa: V, L; tub: V', L') hisoblanadi, eng yomoni olinadi.
 */
import { gasDensity, getSubstance, liquidDensity } from '../../properties';
import type { WarningSink } from '../../types';
import type { RectificationParams, SectionHydraulics } from './types';

export const DEFAULT_SURFACE_TENSION = 0.02;
export const DEFAULT_DOWNCOMER_FRACTION = 0.12;

/** Fair korrelyatsiyasi (Lygeros–Magoulas approksimatsiyasi), m/s */
export function fairCsb(traySpacing: number, flowParameter: number): number {
  const TSmm = traySpacing * 1000;
  const Flv = Math.max(flowParameter, 1e-6);
  return 0.0105 + 8.127e-4 * Math.pow(TSmm, 0.755) * Math.exp(-1.463 * Math.pow(Flv, 0.842));
}

export function netTrayArea(p: RectificationParams): number {
  const fdc = p.downcomerAreaFraction ?? DEFAULT_DOWNCOMER_FRACTION;
  return ((1 - fdc) * Math.PI * p.diameter * p.diameter) / 4;
}

/** Suyuq aralashma zichligi, kg/m³ (x — yengil komponent mol ulushi) */
export function mixtureLiquidDensity(
  light: string,
  heavy: string,
  x: number,
  T: number,
  warnings?: WarningSink,
): number {
  const ML = getSubstance(light).molarMass;
  const MH = getSubstance(heavy).molarMass;
  const mL = x * ML;
  const mH = (1 - x) * MH;
  const wL = mL / (mL + mH);
  const rhoL = liquidDensity(light, T, warnings);
  const rhoH = liquidDensity(heavy, T, warnings);
  return 1 / (wL / rhoL + (1 - wL) / rhoH);
}

export function sectionHydraulics(
  p: RectificationParams,
  args: {
    /** Suyuqlik tarkibi (zichlik uchun) */
    x: number;
    /** Bug' tarkibi (molyar massa uchun) */
    y: number;
    T: number;
    V: number;
    L: number;
  },
  warnings?: WarningSink,
): SectionHydraulics {
  const ML = getSubstance(p.lightComponent).molarMass;
  const MH = getSubstance(p.heavyComponent).molarMass;
  const MV = args.y * ML + (1 - args.y) * MH;
  const MLiq = args.x * ML + (1 - args.x) * MH;
  // gasDensity ideal gaz: ρ = P·M/(R·T) — aralash uchun M_V bilan
  const rhoV = (gasDensity(p.lightComponent, args.T, p.pressure) * MV) / ML;
  const rhoL = mixtureLiquidDensity(p.lightComponent, p.heavyComponent, args.x, args.T, warnings);
  const Vmass = Math.max(args.V, 0) * MV;
  const Lmass = Math.max(args.L, 0) * MLiq;
  const velocity = Vmass / (rhoV * netTrayArea(p));
  const flowParameter = Vmass > 0 ? (Lmass / Vmass) * Math.sqrt(rhoV / rhoL) : 0;
  const cSb = p.cSb ?? fairCsb(p.traySpacing, flowParameter);
  const sigma = p.surfaceTension ?? DEFAULT_SURFACE_TENSION;
  const floodingVelocity =
    cSb * Math.pow(sigma / 0.02, 0.2) * Math.sqrt(Math.max(rhoL - rhoV, 0) / rhoV);
  return {
    vaporMolarFlow: args.V,
    liquidMolarFlow: args.L,
    vaporDensity: rhoV,
    liquidDensity: rhoL,
    vaporVelocity: velocity,
    floodingVelocity,
    floodingFraction: floodingVelocity > 0 ? velocity / floodingVelocity : 0,
    cSb,
    flowParameter,
  };
}
